/**
 * Support chat launcher for the login page.
 *
 * Cora — GetHub's support assistant — is the chat; it replaced Salesforce's
 * Vanessa. For this browser tab (kept across navigation): `?cora=off` falls
 * back to Vanessa below, `?cora=dev` or `?cora=staging` talks to that GetHub
 * instead, `?cora=prod` resets.
 *
 * Vanessa (the fallback):
 * Replaces the legacy Embedded Service Live Agent v5.0 widget
 * (window.embedded_svc) with Agentforce's bootstrap-based loader
 * (window.embeddedservice_bootstrap). LoginPage continues to call the
 * same exported ``loadSalesforceChat`` / ``unloadSalesforceChat`` on
 * mount/unmount so the call sites don't change.
 *
 * The init args + site URL + scrt2 URL come from the Salesforce-side
 * deployment config (Service Console → Agentforce_Vanessa_Pawtected).
 * The ``Origin_Domain`` hidden prechat field is set on the ready event
 * so the agent sees which app the resident is messaging from.
 */

type SetHiddenPrechatFieldsFn = (fields: Record<string, string>) => void;

declare global {
  interface Window {
    GetCoveredSupportAssistant?: { destroy?: () => void };
    embeddedservice_bootstrap?: {
      settings: {
        language?: string;
        [key: string]: unknown;
      };
      init: (
        orgId: string,
        deploymentDevName: string,
        siteUrl: string,
        config: { scrt2URL: string },
      ) => void;
      prechatAPI?: {
        setHiddenPrechatFields: SetHiddenPrechatFieldsFn;
      };
    };
  }
}

const LOADER_SRC =
  'https://getcovered.my.site.com/ESWAgentforceVanessaPaw1781720927714/assets/js/bootstrap.min.js';
const SCRIPT_ID = 'sf-agentforce-chat-loader';

const ORG_ID = '00D6g000001YXyV';
const DEPLOYMENT_DEV_NAME = 'Agentforce_Vanessa_Pawtected';
const SITE_URL = 'https://getcovered.my.site.com/ESWAgentforceVanessaPaw1781720927714';
const SCRT2_URL = 'https://getcovered.my.salesforce-scrt.com';
const LANGUAGE = 'en_US';

const CORA_ENVIRONMENTS = {
  prod: 'https://getcovered.app',
  staging: 'https://staging.getcovered.app',
  dev: 'https://dev.getcovered.app',
} as const;
type CoraEnvironment = keyof typeof CORA_ENVIRONMENTS;
const CORA_OVERRIDE_KEY = 'gc-cora-environment';
const CORA_TAG_ATTRIBUTE = 'data-gc-support-assistant';

const isCoraEnvironment = (value: string | null): value is CoraEnvironment =>
  value !== null && Object.prototype.hasOwnProperty.call(CORA_ENVIRONMENTS, value);
const isCoraOverride = (value: string | null) => value === 'off' || isCoraEnvironment(value);

/** The GetHub environment to load Cora from, or null for Vanessa (`?cora=off`). */
export function coraEnvironment(): CoraEnvironment | null {
  let requested: string | null = null;
  try {
    requested = new URLSearchParams(window.location.search).get('cora');
    if (requested === 'prod') sessionStorage.removeItem(CORA_OVERRIDE_KEY);
    else if (isCoraOverride(requested)) sessionStorage.setItem(CORA_OVERRIDE_KEY, requested);
    requested = sessionStorage.getItem(CORA_OVERRIDE_KEY);
  } catch {
    // No sessionStorage (privacy mode): the query string alone decides.
  }
  if (requested === 'off') return null;
  return isCoraEnvironment(requested) ? requested : 'prod';
}

function loadCora(environment: CoraEnvironment): void {
  if (document.querySelector(`script[${CORA_TAG_ATTRIBUTE}]`)) return;
  // The loader reads its attributes from document.currentScript, so it must be
  // a real classic <script>, not a bundled import.
  const script = document.createElement('script');
  script.src = `${CORA_ENVIRONMENTS[environment]}/support-assistant.js`;
  script.dataset.product = 'pawtected';
  script.setAttribute(CORA_TAG_ATTRIBUTE, '1');
  document.body.appendChild(script);
}

let scriptInjected = false;
let readyListenerAttached = false;

function applyHiddenPrechatFields(): void {
  try {
    window.embeddedservice_bootstrap?.prechatAPI?.setHiddenPrechatFields({
      Origin_Domain: window.location.hostname,
    });
  } catch (err) {
    console.error('Agentforce setHiddenPrechatFields failed', err);
  }
}

function attachReadyListener(): void {
  if (typeof window === 'undefined' || readyListenerAttached) return;
  readyListenerAttached = true;
  // Mirrors the inline snippet from the Service Console: the ready
  // event fires after every init() completes, so re-mounts after an
  // unload still get the hidden field set.
  window.addEventListener('onEmbeddedMessagingReady', applyHiddenPrechatFields);
}

function initEmbeddedMessaging(): void {
  try {
    const bootstrap = window.embeddedservice_bootstrap;
    if (!bootstrap) return;
    bootstrap.settings.language = LANGUAGE;
    bootstrap.init(ORG_ID, DEPLOYMENT_DEV_NAME, SITE_URL, { scrt2URL: SCRT2_URL });
  } catch (err) {
    console.error('Error loading Agentforce Embedded Messaging:', err);
  }
}

export function loadSalesforceChat(): void {
  if (typeof window === 'undefined') return;

  const cora = coraEnvironment();
  if (cora) {
    loadCora(cora);
    return;
  }

  attachReadyListener();

  // Bootstrap already on the page (e.g. user navigated back to /login):
  // re-init so the launcher re-renders without re-fetching the script.
  if (window.embeddedservice_bootstrap) {
    initEmbeddedMessaging();
    return;
  }

  if (scriptInjected) return;
  scriptInjected = true;

  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.src = LOADER_SRC;
  script.async = true;
  script.onload = initEmbeddedMessaging;
  document.body.appendChild(script);
}

export function unloadSalesforceChat(): void {
  if (typeof document === 'undefined') return;
  // Cora removes everything it added; its tag goes too (a tag removed before
  // its script arrived cancels the load).
  window.GetCoveredSupportAssistant?.destroy?.();
  document.querySelectorAll(`script[${CORA_TAG_ATTRIBUTE}]`).forEach((el) => el.remove());
  // Class/id prefix sweep — Agentforce mounts a launcher button, a
  // conversation modal, and iframe wrappers under different class
  // names. Removing everything matching the `embeddedMessaging` prefix
  // catches all of them without us having to enumerate selectors that
  // may shift across vendor releases.
  document
    .querySelectorAll(
      '[class*="embeddedMessaging"], [id*="embeddedMessaging"], [id*="embedded-messaging"]',
    )
    .forEach((el) => el.remove());
}

export {};
